// @client/components/profile-manager/InlineCategoryEditor.tsx
// ✅ FIXED: Direct API calls with proper error handling

import React, { useState, useMemo, useEffect } from "react";
import { X, Check, Search } from "lucide-react";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Badge } from "@client/components/ui/badge";
import { Checkbox } from "@client/components/ui/checkbox";
import { ScrollArea } from "@client/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@client/components/ui/popover";
import { useToast } from "@client/hooks/use-toast";
import { ApiClient } from "@client/lib/api-client";

import type { Category, ProfileCategoryInfo } from "@shared/types";

export interface InlineCategoryEditorProps {
  profileId: number;
  currentCategories?: ProfileCategoryInfo[];
  allCategories: Category[];
  onSave: (profileId: number, categoryIds: number[]) => void | Promise<void>;
  onCancel: () => void;
  showColors?: boolean;
  maxHeight?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function InlineCategoryEditor({
  profileId,
  currentCategories = [],
  allCategories,
  onSave,
  onCancel,
  showColors = true,
  maxHeight = "20rem",
  isOpen = true,
  onOpenChange
}: InlineCategoryEditorProps) {
  
  const { toast } = useToast();
  
  // Extract current category IDs from ProfileCategoryInfo
  const currentCategoryIds = useMemo(() => 
    currentCategories.map(cat => cat.categoryId),
    [currentCategories]
  );
  
  // Local state for selected category IDs
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(currentCategoryIds)
  );
  
  const [searchQuery, setSearchQuery] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Reset when currentCategories change
  useEffect(() => {
    setSelectedIds(new Set(currentCategoryIds));
  }, [currentCategoryIds]);
  
  // Filter available categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return allCategories;
    
    const query = searchQuery.toLowerCase();
    return allCategories.filter(cat => 
      cat.name.toLowerCase().includes(query) ||
      cat.description?.toLowerCase().includes(query)
    );
  }, [allCategories, searchQuery]);
  
  // Get selected category objects from allCategories
  const selectedCategories = useMemo(() => 
    allCategories.filter(cat => selectedIds.has(cat.id)),
    [allCategories, selectedIds]
  );
  
  // Handle category toggle
  const handleToggle = (categoryId: number) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };
  
  // Handle remove from selected
  const handleRemove = (categoryId: number) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(categoryId);
      return newSet;
    });
  };
  
  // ✅ FIXED: Direct API calls with diff calculation
  // ✅ FIXED: Direct API calls with diff calculation + 404 tolerance
  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      const newIds = Array.from(selectedIds);
      const oldIds = currentCategoryIds;
      
      console.log('[InlineCategoryEditor] Saving categories:', {
        profileId,
        oldIds,
        newIds
      });
      
      // Calculate diff
      const toRemove = oldIds.filter(id => !newIds.includes(id));
      const toAdd = newIds.filter(id => !oldIds.includes(id));
      
      console.log('[InlineCategoryEditor] Diff:', { toRemove, toAdd });
      
      // Remove old categories (individual DELETE calls)
      for (const categoryId of toRemove) {
        console.log('[InlineCategoryEditor] Removing category:', categoryId);
        
        try {
          await ApiClient.delete(`/profiles/${profileId}/categories/${categoryId}`);
        } catch (error: any) {
          // ✅ Tolerate 404 - category already removed
          if (error?.status === 404) {
            console.log(`[InlineCategoryEditor] Category ${categoryId} already removed (404) - continuing`);
            continue;
          }
          throw error; // Re-throw other errors
        }
      }
      
      // Add new categories (individual POST calls)
      for (const categoryId of toAdd) {
        console.log('[InlineCategoryEditor] Adding category:', categoryId);
        await ApiClient.post(`/profiles/${profileId}/categories`, {
          categoryId: categoryId
        });
      }
      
      console.log('[InlineCategoryEditor] Save complete');
      
      // Success toast
      toast({
        title: 'Categories updated',
        description: `${toRemove.length} removed, ${toAdd.length} added`,
      });
      
      // Call parent onSave callback (for refetch/cleanup only)
      await onSave(profileId, newIds);
      
    } catch (error: any) {
      console.error('[InlineCategoryEditor] Save failed:', error);
      
      toast({
        title: 'Save failed',
        description: error?.message || 'Could not update categories',
        variant: 'destructive'
      });
      
      // Reset to original state on error
      setSelectedIds(new Set(currentCategoryIds));
      
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    setSelectedIds(new Set(currentCategoryIds));
    setSearchQuery("");
    onCancel();
  };
  
  // Check if changes were made
  const hasChanges = useMemo(() => {
    const originalIds = new Set(currentCategoryIds);
    
    if (originalIds.size !== selectedIds.size) return true;
    
    for (const id of selectedIds) {
      if (!originalIds.has(id)) return true;
    }
    
    return false;
  }, [currentCategoryIds, selectedIds]);
  
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <div />
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        align="start"
        side="bottom"
        onInteractOutside={(e) => {
          if ((e.target as Element).closest('[data-inline-category-editor]')) {
            e.preventDefault();
          }
        }}
      >
        <div data-inline-category-editor className="flex flex-col">
          {/* Header with Search */}
          <div className="p-3 border-b space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Edit Categories</h4>
              {selectedIds.size > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {selectedIds.size} selected
                </Badge>
              )}
            </div>
            
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm"
                autoFocus
              />
            </div>
          </div>
          
          {/* Selected Categories */}
          {selectedCategories.length > 0 && (
            <div className="p-3 border-b bg-muted/30">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Selected Categories
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedCategories.map(category => (
                  <Badge
                    key={category.id}
                    variant="secondary"
                    className="text-xs cursor-pointer hover:bg-secondary/80"
                    style={showColors && category.color ? {
                      backgroundColor: `${category.color}20`,
                      borderColor: category.color,
                      color: category.color
                    } : undefined}
                    onClick={() => handleRemove(category.id)}
                  >
                    {category.name}
                    <X className="ml-1 h-3 w-3" />
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Available Categories */}
          <ScrollArea className="flex-1" style={{ maxHeight }}>
            <div className="p-2 space-y-1">
              {filteredCategories.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  {searchQuery ? 'No categories found' : 'No categories available'}
                </div>
              ) : (
                filteredCategories.map(category => {
                  const isSelected = selectedIds.has(category.id);
                  
                  return (
                    <div
                      key={category.id}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleToggle(category.id)}
                    >
                      <Checkbox
                        id={`category-${category.id}`}
                        checked={isSelected}
                        onCheckedChange={() => handleToggle(category.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {showColors && category.color && (
                          <div
                            className="w-3 h-3 rounded-full border flex-shrink-0"
                            style={{ backgroundColor: category.color }}
                          />
                        )}
                        
                        <label
                          htmlFor={`category-${category.id}`}
                          className="text-sm cursor-pointer flex-1 truncate"
                          title={category.description || category.name}
                        >
                          {category.name}
                        </label>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
          
          {/* Footer Actions */}
          <div className="p-3 border-t flex items-center justify-between gap-2">
            <div className="text-xs text-muted-foreground">
              {hasChanges ? 'Unsaved changes' : 'No changes'}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
              >
                {isSaving ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}